import {Box, VStack} from 'platform/foundation';
import styled from 'styled-components';

import {useMemo} from 'react';

import {useGetUserMenuPinsQuery} from '@dms/api/customer';
import {MenuItemProps, UserSidebarIconSize} from '@dms/api/shared';
import {moduleKeys} from '@dms/config';
import {useFocusControl} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {HideOnMobileNavigation} from '../components/HideOnMobileNavigation';
import {MenuItemsStateProvider} from '../components/MenuItemsStateProvider';
import {SidebarKeyboardControlProvider} from '../components/SidebarKeyboardControlProvider';
import {SidebarMenu} from '../components/SidebarMenu';
import {SidebarNavigation} from '../components/SidebarNavigation';
import {useCreateCustomMenuActions} from '../hooks/useCreateCustomMenuActions';
import {useIsMobileNavigation} from '../hooks/useIsMobileNavigation';
import {addPinsToMenuItems} from '../utils/addPinsToMenuItems';
import {insertMenuItemsAfter} from '../utils/insertMenuItemsAfter';
import {sortMenuItemsByPosition} from '../utils/sortMenuItemsByPosition';
import {transformPinnedMenuItems} from '../utils/transformPinnedMenuItems';

interface SidebarProps extends RequiredTestIdProps {
  iconSize: UserSidebarIconSize;
  menuItems: MenuItemProps[];
  menuItemsById: Record<string, MenuItemProps>;
  createMenuItems: MenuItemProps[];
}

export function Sidebar(props: SidebarProps) {
  const isMobileNavigation = useIsMobileNavigation();
  const {createCustomMenuActions, createCustomMenuFastAccessItems} = useCreateCustomMenuActions();
  const {isDisabled} = useFocusControl();

  const {data: pinnedMenuItems = []} = useGetUserMenuPinsQuery();

  const filteredMenuItems = props.menuItems.filter(
    (menuItem) =>
      // Setting have been moved to the top bar.
      menuItem.id !== moduleKeys.settings
  );

  const menuItems = insertMenuItemsAfter(
    filteredMenuItems,
    moduleKeys.vehicles,
    createCustomMenuActions
  );

  const menuItemsByIdWithCustomActions = useMemo(() => {
    const customActionsById = createCustomMenuActions.reduce(
      (acc, item) => {
        acc[item.id] = item;
        return acc;
      },
      {} as Record<string, MenuItemProps>
    );

    return {...props.menuItemsById, ...customActionsById};
  }, [props.menuItemsById, createCustomMenuActions]);

  const menuItemsWithPins = addPinsToMenuItems(menuItems, pinnedMenuItems);
  const sidebarPinnedMenuItems = transformPinnedMenuItems(
    pinnedMenuItems,
    menuItemsByIdWithCustomActions
  );

  const createMenuItems = sortMenuItemsByPosition([
    ...props.createMenuItems,
    ...createCustomMenuFastAccessItems,
  ]);

  return (
    <MenuItemsStateProvider>
      <SidebarKeyboardControlProvider
        sidebarPinnedMenuItems={sidebarPinnedMenuItems}
        menuItemsWithPins={menuItemsWithPins}
        createMenuItems={createMenuItems}
      >
        {(sidebarRef, onKeyDown) => (
          <>
            <SidebarContainer
              $isMobile={isMobileNavigation}
              ref={sidebarRef}
              onKeyDown={onKeyDown}
              tabIndex={isDisabled ? -1 : 0}
            >
              <Box
                height="100%"
                contain="layout"
                zIndex="MAIN_MENU"
                position="relative"
                backgroundColor="palettes.neutral.900.100"
                data-testid={suffixTestId('menuWrapper', props)}
              >
                <VStack height="100%">
                  <SidebarMenu
                    iconSize={props.iconSize}
                    menuItemsWithPins={menuItemsWithPins}
                    createMenuItems={createMenuItems}
                    menuItemsById={menuItemsByIdWithCustomActions}
                    data-testid={props['data-testid']}
                  />
                  <HideOnMobileNavigation>
                    <SidebarNavigation
                      iconSize={props.iconSize}
                      menuItemsById={menuItemsByIdWithCustomActions}
                      pinnedMenuItems={sidebarPinnedMenuItems}
                      createMenuItems={createMenuItems}
                      data-testid={props['data-testid']}
                    />
                  </HideOnMobileNavigation>
                </VStack>
              </Box>
            </SidebarContainer>
          </>
        )}
      </SidebarKeyboardControlProvider>
    </MenuItemsStateProvider>
  );
}

const SidebarContainer = styled.div<{$isMobile: boolean}>`
  grid-column-end: span 1;
  grid-row-end: span ${({$isMobile}) => ($isMobile ? 1 : 2)};

  &:focus {
    outline: none;
  }
`;
